import * as React from 'react';
import type { FormattedMessage } from 'react-intl';

import type { invitedSignUp } from 'state/actions/invited-sign-up';
import type { InvitedSignUpSubmitState } from 'state/ui/invited-sign-up';
import { ScreenName } from '@atlassian/help-center-common-component/constants';
import type { InjectedProps as BrowserMetricsScreenTransitionStatusProps } from '@atlassian/help-center-common-util/analytics/browsermetrics-screen-transition-status-context';
import {
    validateAll,
    validateFullName,
    validatePassword,
} from '@atlassian/help-center-common-util/sign-up-form-validator';
import { InvitedSignUpFormStateless } from '../invited-sign-up-form-stateless';
import { retrieveUrlParams } from '../invited-sign-up-url-param-retriever';

interface InvitedSignUpFormProps {
    portalId: number;
    email: string;
    formSubmitState: InvitedSignUpSubmitState;
    contactLink?: string;
    invitedSignUpAction: typeof invitedSignUp;
}

interface State {
    fullName: string;
    fullNameError?: FormattedMessage.MessageDescriptor;
    password: string;
    passwordError?: FormattedMessage.MessageDescriptor;
}

export default class InvitedSignUpForm extends React.Component<
    InvitedSignUpFormProps & BrowserMetricsScreenTransitionStatusProps,
    State
> {
    state = {
        fullName: '',
        fullNameError: undefined,
        password: '',
        passwordError: undefined,
    };

    onFullNameFieldChange = (updatedFullName: string) => {
        this.setState({
            fullName: updatedFullName,
        });

        if (this.state.fullNameError) {
            const updatedFullNameError = validateFullName(updatedFullName);
            this.setState({
                fullNameError: updatedFullNameError,
            });
        }
    };

    onPasswordFieldChange = (updatedPassword: string) => {
        this.setState({
            password: updatedPassword,
        });

        if (this.state.passwordError) {
            const updatedPasswordError = validatePassword(updatedPassword);
            this.setState({
                passwordError: updatedPasswordError,
            });
        }
    };

    validateFields = () => {
        const { fullName, password } = this.state;
        const validationErrors = validateAll(fullName, password);
        if (validationErrors) {
            this.setState({
                ...validationErrors,
            });
        } else {
            this.setState({
                fullNameError: undefined,
                passwordError: undefined,
            });
        }

        return validationErrors !== undefined;
    };

    onSubmit = () => {
        const validationErrorsPresent = this.validateFields();
        if (validationErrorsPresent) {
            return;
        }

        this.props.startBrowserMetricsTransitionToNewScreen(ScreenName.PORTAL_HOME);

        const { portalId, email, contactLink, invitedSignUpAction } = this.props;
        // Suppressing existing violation. Please fix this.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { token, username } = retrieveUrlParams();
        const contactAdminsFormEnabled = contactLink !== undefined;

        const { fullName, password } = this.state;

        invitedSignUpAction(portalId, email, fullName, password, token, username, contactAdminsFormEnabled);
    };

    render() {
        const { email, formSubmitState } = this.props;
        const { fullName, fullNameError, password, passwordError } = this.state;

        return (
            <InvitedSignUpFormStateless
                email={email}
                fullName={fullName}
                fullNameError={fullNameError}
                onFullNameChange={this.onFullNameFieldChange}
                password={password}
                passwordError={passwordError}
                onPasswordChange={this.onPasswordFieldChange}
                onSubmit={this.onSubmit}
                isSubmitting={formSubmitState.isSubmitting}
            />
        );
    }
}
